import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, IconButton, Chip, FormControlLabel, Checkbox,
  Snackbar, Alert,
} from '@mui/material';
import { Add, Edit, Upload, Delete, Download } from '@mui/icons-material';
import api from '../../api/client';

interface CallType {
  id: number; code: string; name: string; priority: number; is_active: boolean;
  sort_order: number; status_check: boolean; status_check_interval: number;
  available_for_field_incident: boolean;
  dispatch_law: boolean; dispatch_fire: boolean; dispatch_ems: boolean;
}

const PRIORITY_COLORS: Record<number, string> = { 1: '#f44336', 2: '#ff9800', 3: '#2196f3', 4: '#4caf50', 5: '#9e9e9e' };

export default function CallTypesPage() {
  const [types, setTypes] = useState<CallType[]>([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<CallType | null>(null);
  const [form, setForm] = useState({ code: '', name: '', priority: 3, sort_order: 0, status_check: false, status_check_interval: 15, available_for_field_incident: false, dispatch_law: true, dispatch_fire: false, dispatch_ems: false });
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [snack, setSnack] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const load = () => api.get('/call-types').then((r) => { setTypes(r.data); setSelected(new Set()); });
  useEffect(() => { load(); }, []);

  const handleOpen = (ct?: CallType) => {
    if (ct) {
      setEditing(ct);
      setForm({ code: ct.code, name: ct.name, priority: ct.priority, sort_order: ct.sort_order, status_check: ct.status_check, status_check_interval: ct.status_check_interval, available_for_field_incident: ct.available_for_field_incident, dispatch_law: ct.dispatch_law, dispatch_fire: ct.dispatch_fire, dispatch_ems: ct.dispatch_ems });
    } else {
      setEditing(null);
      setForm({ code: '', name: '', priority: 3, sort_order: 0, status_check: false, status_check_interval: 15, available_for_field_incident: false, dispatch_law: true, dispatch_fire: false, dispatch_ems: false });
    }
    setOpen(true);
  };

  const handleSave = async () => {
    if (editing) {
      await api.patch(`/call-types/${editing.id}`, form);
    } else {
      await api.post('/call-types', form);
    }
    setOpen(false);
    load();
  };

  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await api.post('/call-types/import-csv', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setSnack(`Imported: ${r.data.created} created, ${r.data.skipped} skipped`);
      load();
    } catch { setSnack('Import failed'); }
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleBulkDelete = async () => {
    if (!selected.size || !window.confirm(`Delete ${selected.size} call type(s)?`)) return;
    await api.post('/call-types/bulk-delete', { ids: Array.from(selected) });
    setSnack(`Deleted ${selected.size} call type(s)`);
    load();
  };

  const toggleSelect = (id: number) => {
    const s = new Set(selected);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelected(s);
  };
  const toggleAll = () => {
    if (selected.size === types.length) setSelected(new Set());
    else setSelected(new Set(types.map((t) => t.id)));
  };

  const handleExport = () => {
    const headers = ['code', 'name', 'priority', 'sort_order', 'status_check', 'status_check_interval'];
    const rows = types.map((ct) => [
      ct.code, ct.name, ct.priority, ct.sort_order,
      ct.status_check ? 'yes' : 'no', ct.status_check_interval,
    ].map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','));
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'call_types.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Call Types</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {selected.size > 0 && (
            <Button startIcon={<Delete />} color="error" variant="outlined" onClick={handleBulkDelete}>
              Delete ({selected.size})
            </Button>
          )}
          <input type="file" accept=".csv" ref={fileRef} hidden onChange={handleImport} />
          <Button startIcon={<Download />} variant="outlined" onClick={handleExport}>Export CSV</Button>
          <Button startIcon={<Upload />} variant="outlined" onClick={() => fileRef.current?.click()}>Import CSV</Button>
          <Button startIcon={<Add />} variant="contained" onClick={() => handleOpen()}>Add Call Type</Button>
        </Box>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox"><Checkbox checked={types.length > 0 && selected.size === types.length} indeterminate={selected.size > 0 && selected.size < types.length} onChange={toggleAll} /></TableCell>
              <TableCell>Code</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Default Priority</TableCell>
              <TableCell>Sort Order</TableCell>
              <TableCell>Status Check</TableCell>
              <TableCell>Field Incident</TableCell>
              <TableCell>Disciplines</TableCell>
              <TableCell>Status</TableCell>
              <TableCell width={60} />
            </TableRow>
          </TableHead>
          <TableBody>
            {types.map((ct) => (
              <TableRow key={ct.id} selected={selected.has(ct.id)}>
                <TableCell padding="checkbox"><Checkbox checked={selected.has(ct.id)} onChange={() => toggleSelect(ct.id)} /></TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{ct.code}</TableCell>
                <TableCell>{ct.name}</TableCell>
                <TableCell><Chip size="small" label={`P${ct.priority}`} sx={{ bgcolor: PRIORITY_COLORS[ct.priority] || '#9e9e9e', color: '#fff' }} /></TableCell>
                <TableCell>{ct.sort_order}</TableCell>
                <TableCell>
                  {ct.status_check ? (
                    <Chip size="small" label={`Every ${ct.status_check_interval} min`} color="warning" />
                  ) : (
                    <Chip size="small" label="Off" variant="outlined" />
                  )}
                </TableCell>
                <TableCell>
                  {ct.available_for_field_incident ? (
                    <Chip size="small" label="Yes" color="success" />
                  ) : (
                    <Chip size="small" label="No" variant="outlined" />
                  )}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {ct.dispatch_law && <Chip size="small" label="Law" sx={{ bgcolor: '#1565c0', color: '#fff' }} />}
                    {ct.dispatch_fire && <Chip size="small" label="Fire" sx={{ bgcolor: '#c62828', color: '#fff' }} />}
                    {ct.dispatch_ems && <Chip size="small" label="EMS" sx={{ bgcolor: '#2e7d32', color: '#fff' }} />}
                    {!ct.dispatch_law && !ct.dispatch_fire && !ct.dispatch_ems && <Chip size="small" label="None" variant="outlined" />}
                  </Box>
                </TableCell>
                <TableCell><Chip size="small" label={ct.is_active ? 'Active' : 'Inactive'} color={ct.is_active ? 'success' : 'default'} /></TableCell>
                <TableCell><IconButton size="small" onClick={() => handleOpen(ct)}><Edit fontSize="small" /></IconButton></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Call Type' : 'New Call Type'}</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Code" margin="normal" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="e.g. 10-50" />
          <TextField fullWidth label="Name" margin="normal" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Traffic Accident" />
          <TextField fullWidth label="Default Priority (1=Emergency, 5=Info)" type="number" margin="normal" value={form.priority} onChange={(e) => setForm({ ...form, priority: Number(e.target.value) })} slotProps={{ htmlInput: { min: 1, max: 5 } }} />
          <TextField fullWidth label="Sort Order" type="number" margin="normal" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) })} />
          <FormControlLabel
            control={<Checkbox checked={form.status_check} onChange={(e) => setForm({ ...form, status_check: e.target.checked })} />}
            label="Enable Status Check Reminders"
            sx={{ mt: 1, display: 'block' }}
          />
          {form.status_check && (
            <TextField
              fullWidth
              label="Status Check Interval (minutes)"
              type="number"
              margin="normal"
              value={form.status_check_interval}
              onChange={(e) => setForm({ ...form, status_check_interval: Math.max(1, Number(e.target.value)) })}
              slotProps={{ htmlInput: { min: 1 } }}
              helperText="How often (in minutes) to remind dispatch to check on units on scene for this call type"
            />
          )}
          <FormControlLabel
            control={<Checkbox checked={form.available_for_field_incident} onChange={(e) => setForm({ ...form, available_for_field_incident: e.target.checked })} />}
            label="Available for Field Incident (MDT)"
            sx={{ mt: 1, display: 'block' }}
          />
          <Typography variant="subtitle2" sx={{ mt: 2, fontWeight: 700 }}>Dispatch Disciplines</Typography>
          <Typography variant="caption" color="text.secondary">
            Which emergency service zones (Law / Fire / EMS) this call type resolves and dispatches.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
            <FormControlLabel
              control={<Checkbox checked={form.dispatch_law} onChange={(e) => setForm({ ...form, dispatch_law: e.target.checked })} />}
              label="Law"
            />
            <FormControlLabel
              control={<Checkbox checked={form.dispatch_fire} onChange={(e) => setForm({ ...form, dispatch_fire: e.target.checked })} />}
              label="Fire"
            />
            <FormControlLabel
              control={<Checkbox checked={form.dispatch_ems} onChange={(e) => setForm({ ...form, dispatch_ems: e.target.checked })} />}
              label="EMS"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>Save</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!snack} autoHideDuration={4000} onClose={() => setSnack('')}>
        <Alert severity="info" onClose={() => setSnack('')}>{snack}</Alert>
      </Snackbar>
    </Box>
  );
}
